/**
 * NotificationDropdown
 * Dropdown with user notifications in header
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { notificationService } from '@/services/notification-service';
import { Notification } from '@/types/notification';
import { NotificationItem } from './NotificationItem';
import toast from '@/lib/toast';

export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const requestSeqRef = useRef(0);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch {
      // Keep the header quiet; the full notifications page surfaces load errors.
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    try {
      setLoading(true);
      const response = await notificationService.getAll(
        { archived: false },
        { page: 1, limit: 10 }
      );
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      setNotifications(response.data.notifications);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Load unread count
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => {
        const notification = notifications.find(n => n.id === id);
        return notification?.read ? prev : Math.max(0, prev - 1);
      });
    } catch (error) {
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await notificationService.delete(id);
      const notification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      toast.success('Notificação excluída');
    } catch (error) {
      toast.error('Erro ao excluir notificação');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        type="button"
        variant="ghost"
        size="icon-lg"
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-muted-foreground"
        aria-label="Notificações"
        aria-expanded={isOpen}
      >
        <Bell className="size-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex size-5 items-center justify-center rounded-full bg-destructive text-xs font-semibold text-background">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Backdrop (mobile only) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-xs sm:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed inset-x-3 top-16 z-50 flex max-h-[calc(100vh-5rem)] flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:max-h-none sm:w-96">
          {/* Header */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              Notificações {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            {unreadCount > 0 && (
              <Button type="button" variant="link" size="xs" onClick={handleMarkAllAsRead} className="h-auto px-0">
                <Check />
                Marcar todas como lidas
              </Button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 sm:max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex flex-col gap-2 p-4" aria-busy="true">
                <span className="sr-only">Carregando notificações...</span>
                {[0, 1, 2].map(i => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                Nenhuma notificação
              </div>
            ) : (
              <>
                {/* Unread */}
                {unreadNotifications.length > 0 && (
                  <>
                    {unreadNotifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        onDelete={handleDelete}
                      />
                    ))}
                  </>
                )}

                {/* Read */}
                {readNotifications.length > 0 && (
                  <>
                    {unreadNotifications.length > 0 && (
                      <div className="px-4 py-2 bg-muted text-xs font-medium text-muted-foreground">
                        Anteriores
                      </div>
                    )}
                    {readNotifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        onDelete={handleDelete}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="shrink-0 border-t border-border px-4 py-2 text-center">
              <Button variant="link" size="sm" asChild>
                <Link to="/notifications" onClick={() => setIsOpen(false)}>
                  Ver todas as notificações
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
